import { page } from '$app/state';

import {
  PUBLIC_FREE_SHIPPING_LIMIT_SE,
  PUBLIC_FREE_SHIPPING_LIMIT_NO,
  PUBLIC_FREE_SHIPPING_LIMIT_DK,
  PUBLIC_FREE_SHIPPING_LIMIT_FI,
  PUBLIC_FREE_SHIPPING_LIMIT_ES,
  PUBLIC_FREE_SHIPPING_LIMIT_GB,
  PUBLIC_FREE_SHIPPING_LIMIT_DE,
  PUBLIC_SHIPPING_FEE_SE,
  PUBLIC_SHIPPING_FEE_NO,
  PUBLIC_SHIPPING_FEE_DK,
  PUBLIC_SHIPPING_FEE_FI,
  PUBLIC_SHIPPING_FEE_ES,
  PUBLIC_SHIPPING_FEE_GB,
  PUBLIC_SHIPPING_FEE_DE
} from '$env/static/public';

import type { DiscountCode, SessionData } from '@agapi/mlc-core';

import type { CartStore, CartStoreContent, StoreCartItem, Summary } from './cart-store.types';
import { addCartItem, updateCartItem, removeCartItem, addDiscountCode as addDiscountCodeToCart, removeDiscountCode as removeDiscountCodeToCart } from '../../services/shop.client';
import { getCommerce } from '../../services/commerce.shared';
import { getLocaleFromUrl, transformToCurrency } from '../../utils/locale.shared';
import { getCookie } from '../../utils/cookies.client';
import { variantToCartItem } from '../../utils/transformer.client';

import { freeShipping } from '$lib/parachute/messages';
import { getContext } from 'svelte';
import type { InstanceStore } from '../instance';

const FREE_SHIPPING_LIMITS = {
  SE: PUBLIC_FREE_SHIPPING_LIMIT_SE,
  NO: PUBLIC_FREE_SHIPPING_LIMIT_NO,
  DK: PUBLIC_FREE_SHIPPING_LIMIT_DK,
  FI: PUBLIC_FREE_SHIPPING_LIMIT_FI,
  ES: PUBLIC_FREE_SHIPPING_LIMIT_ES,
  GB: PUBLIC_FREE_SHIPPING_LIMIT_GB,
  DE: PUBLIC_FREE_SHIPPING_LIMIT_DE
}

const SHIPPING_FEES = {
  SE: PUBLIC_SHIPPING_FEE_SE,
  NO: PUBLIC_SHIPPING_FEE_NO,
  DK: PUBLIC_SHIPPING_FEE_DK,
  FI: PUBLIC_SHIPPING_FEE_FI,
  ES: PUBLIC_SHIPPING_FEE_ES,
  GB: PUBLIC_SHIPPING_FEE_GB,
  DE: PUBLIC_SHIPPING_FEE_DE
}

const getVariant = async (sku: string) => {
  try {
    const identifier = getCookie<string>('__agapi_identifier') ?? 'default';
    const locale = getLocaleFromUrl(page.url);
    return await getCommerce(window.fetch).getProductVariant(sku, locale, { userId: identifier });
  } catch (e) {
    console.error('[cart-store:getVariant:error]', e);
    return { item: undefined };
  }
}

const updateDiscountCodes = (updatedDiscountCodes: DiscountCode[], discountCodes: Record<string, DiscountCode>) => {
  // Create a Set of valid discount codes from session
  const validCodes = new Set(updatedDiscountCodes.map(code => code.code));

  // Remove codes that no longer exist in session
  Object.keys(discountCodes).forEach(code => {
    if (!validCodes.has(code)) {
      delete discountCodes[code];
    }
  });

  // Update existing codes with latest values from session
  for (const code of updatedDiscountCodes) {
    discountCodes[code.code] = code;
  }
}

const getFreeShippingLimit = (countryCode: string) => {
  return FREE_SHIPPING_LIMITS[countryCode as keyof typeof FREE_SHIPPING_LIMITS];
}

const getShippingFee = (countryCode: string) => {
  return SHIPPING_FEES[countryCode as keyof typeof SHIPPING_FEES];
}

/**
 * Creates a Svelte store for managing the shopping cart state.
 * @returns An object containing the cart's content and methods to manipulate it.
 */
export function createCartStore(): CartStore {
  console.log('createCartStore');

  // TODO: retrieve FREE_SHIPPING_LIMIT and SHIPPING_FEE from mlc/backend

  let addToCartDebouncer: Record<string, NodeJS.Timeout> = {};
  let updateCartDebouncer: Record<string, NodeJS.Timeout> = {};

  const { country: countryCode } = getLocaleFromUrl(page.url);

  const state = $state<CartStoreContent>({
    timestamp: Date.now(),
    items: [],
    loading: true,
    discountCodes: {},
    summary: {
      freeShippingLimit: getFreeShippingLimit(countryCode),
      shippingCost: getShippingFee(countryCode),
      itemsDiscount: 0,
      couponDiscount: 0,
      totalAmount: 0,
      finalAmount: 0
    }
  });

  // Event handler for BroadcastChannel (syncing cart state with other tabs/windows)
  const instance = getContext<InstanceStore>('instance-store');
  instance.on('cart', async (event: object) => {
    // console.log('[cart-store:instance:cart:listener]', data);
    const data = event as {
      type: 'add' | 'update' | 'remove',
      changes: {
        updatedCart: Record<string, StoreCartItem>;
        updatedDiscountCodes: DiscountCode[];
      }
    } | {
      type: 'add-discount-code' | 'remove-discount-code',
      discountCode: DiscountCode
    } | {
      type: unknown;
      changes: never;
      discountCode: never;
    }
    // console.log('[cart-store:instance:cart:listener]', type, item);

    switch (data.type) {
      case 'add':
      case 'update':
      case 'remove': {
        const { updatedCart, updatedDiscountCodes } = data.changes;
        // console.log('add/update', productVariant, item.quantity);
        await syncCart(updatedCart, updatedDiscountCodes);
        break;
      }
      case 'add-discount-code': {
        // console.log('[cart-store:instance:cart:listener:add-discount-code]', item);
        const discountCode = data.discountCode;
        state.discountCodes[discountCode.code] = discountCode;
        sync();
        state.timestamp = Date.now();
        break;
      }
      case 'remove-discount-code': {
        // console.log('[cart-store:instance:cart:listener:remove-discount-code]', item);
        const discountCode = data.discountCode;
        delete state.discountCodes[discountCode.code];
        sync();
        state.timestamp = Date.now();
        break;
      }
      default: {
        console.log('[cart-store:instance:cart:listener]: unknown', data.type);
      }
    }
  });

  /**
   * Updates the summary of the cart, including total amount, shipping cost, total savings, and final amount.
   * This function should be called after any changes to the cart items.
   */
  const sync = () => {
    // const totalAmount = state.items.reduce((sum, item) => sum + (item.price.amount + (item.price.discount ?? 0)) * item.quantity, 0);
    const totalAmount = state.items.reduce((sum, item) => sum + item.price.amount * item.quantity, 0);
    const itemsDiscount = state.items.reduce((sum, item) => sum + (item.price.discount ?? 0) * item.quantity, 0);
    const couponDiscount = Object.values(state.discountCodes).reduce((sum, code) => sum + code.value, 0);

    // Calculate final amount after all discounts for free shipping check
    const discountedTotal = totalAmount ? totalAmount - itemsDiscount - couponDiscount : 0;

    // Use discounted total for free shipping calculation
    const { country: countryCode } = getLocaleFromUrl(page.url);
    const shippingCost = discountedTotal > getFreeShippingLimit(countryCode) ? 0 : getShippingFee(countryCode);

    state.summary.itemsDiscount = itemsDiscount;
    state.summary.couponDiscount = couponDiscount;
    state.summary.shippingCost = shippingCost;
    state.summary.totalAmount = totalAmount;

    state.summary.finalAmount = discountedTotal; // + (state.items.length > 0 ? state.summary.shippingCost : 0);
  }

  const syncCartUsingSession = async (session: SessionData): Promise<{
    updatedCart: Record<string, StoreCartItem>;
    updatedDiscountCodes: DiscountCode[];
  }> => {
    const updatedCart: Record<string, StoreCartItem> = {};
    for (const item of session.cart.items) {
      // NOTE: we probably want a custom attribute in brink instead of fetching it from loop
      const { item: productVariant } = await getVariant(item.sku);
      if (productVariant) {
        updatedCart[item.sku] = variantToCartItem(productVariant, item.quantity, item.id);
      }
    }

    return syncCart(updatedCart, session.cart.discountCodes);
  }

  const syncCart = async (updatedCart: Record<string, StoreCartItem>, updatedDiscountCodes: DiscountCode[]) => {
    // Remove items that are not in the updated cart
    for (const item of state.items) {
      if (!updatedCart[item.sku]) {
        state.items.splice(state.items.indexOf(item), 1);
      }
    }

    // Add or update items that are in the updated cart
    for (const [sku, cartItem] of Object.entries(updatedCart)) {
      const item = state.items.find((i) => i.sku === sku);
      if (item) {
        Object.assign(item, cartItem);
      } else {
        state.items.push(cartItem);
      }
    }

    // Update discount codes
    updateDiscountCodes(updatedDiscountCodes, state.discountCodes);

    // Sync price summary
    sync();

    state.timestamp = Date.now();

    return { updatedCart, updatedDiscountCodes };
  }

  /**
   * Adds a new item to the cart.
   * @this - The cart store content object.
   * @param item - The item to be added to the cart.
   */
  const add = async (item: StoreCartItem): Promise<void> => {
    return new Promise((res, rej) => {
      console.log('[cart-store] add', item.sku, item.quantity);

      const index = state.items.findIndex((v) => v.sku === item.sku);
      if (index === -1) {
        if (addToCartDebouncer[item.sku]) {
          clearTimeout(addToCartDebouncer[item.sku]);
        }

        addToCartDebouncer[item.sku] = setTimeout(async () => {
          console.log('[cart-store] prepare to add', item.sku);
          try {
            const { language: languageCode, country: countryCode } = getLocaleFromUrl(page.url);
            const { session } = await addCartItem(fetch, item, { languageCode, countryCode });
            console.log('[cart-store] added successfully');

            const changes = await syncCartUsingSession(session);
            console.log('[cart-store] just added', (session.cart.items.find((i) => i.sku === item.sku))?.id);

            instance.post('cart', { type: 'add', changes });
            return res(void 0);
          } catch(e) {
            return rej(e);
          }
        }, 100);
      } else {
        console.log('[cart-store] tried to add but items already exists, updating instead', item.sku, item.quantity);
        return res(update(item, state.items[index].quantity + item.quantity));
      }
    });
  };

  /**
   * Updates an existing item in the cart.
   * @this - The cart store content object.
   * @param item - The item to be updated in the cart.
   */
  const update = (item: StoreCartItem, quantity: number): Promise<void> => {
    return new Promise((res, rej) => {
      console.log('[cart-store] update', item.sku, quantity);

      const index = state.items.findIndex((v) => v.sku === item.sku);
      if (index !== -1) {
        console.log('update', item.sku, quantity);

        if (updateCartDebouncer[item.sku]) {
          clearTimeout(updateCartDebouncer[item.sku]);
        }

        updateCartDebouncer[item.sku] = setTimeout(async () => {
          console.log('[cart-store] prepare to update', item.sku);

          const { language: languageCode, country: countryCode } = getLocaleFromUrl(page.url);
          try {
            const { session } = await updateCartItem(fetch, { ...item, quantity }, { languageCode, countryCode });
            console.log('[cart-store] updated successfully');

            const changes = await syncCartUsingSession(session);
            console.log('[cart-store] just updated', (session.cart.items.find((i) => i.sku === item.sku))?.id);

            instance.post('cart', { type: 'update', changes });
            return res(void 0);
          } catch(e) {
            return rej(e);
          }
        }, 500)
      } else {
        console.log('[cart-store] tried to update but items not found, adding instead', item.sku, quantity);
        return res(add(item));
      }
    });
  };

  /**
   * Removes an item from the cart.
   * @this - The cart store content object.
   * @param item - The item to be removed from the cart.
   */
  const remove = (item: StoreCartItem): Promise<void> => {
    return new Promise(async (res, rej) => {
      console.log('[cart-store] remove', item.sku);

      const index = state.items.findIndex((v) => v.sku === item.sku);
      if (index > -1) {
        console.log('[cart-store] prepare to remove', item.sku);
        try {
          const { language: languageCode, country: countryCode } = getLocaleFromUrl(page.url);
          const { session } = await removeCartItem(fetch, item, { languageCode, countryCode });
          console.log('[cart-store] removed successfully');

          const changes = await syncCartUsingSession(session);
          console.log('[cart-store] just removed', item.id);

          instance.post('cart', { type: 'remove', changes });
          return res(void 0);
        } catch(e) {
          return rej(e);
        }
      } else {
        console.log('[cart-store] tried to remove but items not found', item.sku);
        return res(void 0);
      }
    });
  };

  const addDiscountCode = async (code?: string) => {
    const sessionToken = getCookie<string>('__agapi_session');
    if (code && sessionToken) {
      try {
        console.log('addDiscountCode', code);
        const { language: languageCode, country: countryCode } = getLocaleFromUrl(page.url);
        const response = await addDiscountCodeToCart(fetch, code, { languageCode, countryCode });
        if (response?.session) {
          const discountCode = response.session.cart.discountCodes.find((d) => d.code === code);
          if (discountCode) {
            state.discountCodes[discountCode.code] = discountCode;
            sync();

            state.timestamp = Date.now();

            instance.post('cart', { type: 'add-discount-code', discountCode });
            return true;
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
    return false;
  }

  const removeDiscountCode = async (code?: string) => {
    const sessionToken = getCookie<string>('__agapi_session');
    if (code && sessionToken) {
      try {
        console.log('removeDiscountCode', code);
        const { language: languageCode, country: countryCode } = getLocaleFromUrl(page.url);
        const session = await removeDiscountCodeToCart(fetch, code, { languageCode, countryCode });

        if (session) {
          const discountCode = state.discountCodes[code];
          delete state.discountCodes[code];
          sync();

          state.timestamp = Date.now();

          instance.post('cart', { type: 'remove-discount-code', discountCode });
          return true;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return false;
  }

  /**
   * Computed properties that returns a formatted summary of the cart contents.
   * Uses the Intl.NumberFormat to format currency values according to the 'sv-SE' locale.
   * @type {FormattedSummary}
   */
  const formattedSummary: Omit<Summary<string>, 'freeShippingLimit'> & { amountUntilFreeShipping: string; } = {
    get shippingCost() {
      // NOTICE: we can use countryLocale to ensures the currency symbol is correct
      const { shippingCost } = state.summary;
      return shippingCost ? transformToCurrency(shippingCost / 100, getLocaleFromUrl(page.url)) : freeShipping();
    },
    get itemsDiscount() {
      return transformToCurrency(-state.summary.itemsDiscount / 100, getLocaleFromUrl(page.url));
    },
    get couponDiscount() {
      return transformToCurrency(-state.summary.couponDiscount / 100, getLocaleFromUrl(page.url));
    },
    get totalAmount() {
      return transformToCurrency(state.summary.totalAmount / 100, getLocaleFromUrl(page.url));
    },
    get finalAmount() {
      return transformToCurrency(state.summary.finalAmount / 100, getLocaleFromUrl(page.url));
    },
    get amountUntilFreeShipping() {
      return transformToCurrency((state.summary.freeShippingLimit - state.summary.finalAmount) / 100, getLocaleFromUrl(page.url));
    }
  }

  const clear = () => {
    state.items = [];
    Object.keys(state.discountCodes).forEach(code => delete state.discountCodes[code]);
    state.timestamp = Date.now();
    sync();
  }

  const setLoading = (loading: boolean) => {
    state.loading = loading;
  }

  return {
    get discountCode() {
      return Object.values(state.discountCodes)[0];
    },
    set discountCode(value: DiscountCode) {
      console.log('[cart-store] set discountCode', value);
      state.discountCodes[value.code] = value;
    },
    get timestamp() {
      return state.timestamp;
    },
    set timestamp(value: number) {
      state.timestamp = value;
    },
    get items() {
      return state.items;
    },
    get loading() {
      return state.loading;
    },
    summary: {
      get raw() {
        return state.summary;
      },
      formatted: formattedSummary,
    },
    add,
    update,
    remove,
    addDiscountCode,
    removeDiscountCode,
    sync,
    clear,
    setLoading
  };
}
